<template>
  <div class="py-2 px-2 cursor-pointer" @click="expanded = !expanded">
    <div class="font-semibold text-gray-800">
      <span :class="dateClass">{{ actionDate }}</span><span v-if="actionDate && actionName"> · </span>{{ actionName }}
    </div>
    <div v-if="actionUser" class="text-xs text-gray-500">{{ actionUser }}</div>
    <div v-if="crmAction.action_notes" ref="notesEl" class="text-gray-700">
      <div v-if="expanded" class="whitespace-pre-wrap break-words">{{ crmAction.action_notes }}</div>
      <div v-else class="overflow-hidden whitespace-nowrap">
        {{ collapsedNotes }}<span v-if="isTruncated" class="ml-2 font-bold text-accent-primary">…</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import { formatDate, getTodaysDateAsString } from '@/composables/formatDate';

const props = defineProps({
  crmAction: Object
})

const expanded = ref(false)
const notesEl = ref(null)
const collapsedNotes = ref('')
const isTruncated = ref(false)
let measureContext = null

function measure(text) {
  return measureContext.measureText(text).width
}

function fitNotes() {
  if (!notesEl.value) {
    return
  }
  const text = (props.crmAction.action_notes || '').replace(/\s+/g, ' ').trim()
  const style = getComputedStyle(notesEl.value)
  measureContext = measureContext || document.createElement('canvas').getContext('2d')
  measureContext.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
  const width = notesEl.value.clientWidth

  if (measure(text) <= width) {
    collapsedNotes.value = text
    isTruncated.value = false
    return
  }

  const available = width - measure('…') - 12
  let fitted = ''
  for (const character of text) {
    if (measure(fitted + character) > available) break
    fitted += character
  }
  collapsedNotes.value = fitted.trimEnd()
  isTruncated.value = true
}

useResizeObserver(notesEl, fitNotes)

const isOpen = computed(() => !props.crmAction.resolved_date)

const isOverdue = computed(() => isOpen.value && !!props.crmAction.due_date && props.crmAction.due_date < getTodaysDateAsString())

const actionDate = computed(() => {
  if (!isOpen.value) {
    return formatDate(props.crmAction.resolved_date)
  }
  return props.crmAction.due_date ? `Due ${formatDate(props.crmAction.due_date)}` : 'Due'
})

const dateClass = computed(() => {
  if (isOverdue.value) return 'text-red-600'
  if (isOpen.value) return 'text-amber-600'
  return ''
})

const actionName = computed(() => {
  const action = props.crmAction.action
  if (!action) {
    return ''
  }
  return isOpen.value ? (action.unresolved_label || action.action_name) : action.action_name
})

const actionUser = computed(() => {
  const user = props.crmAction.user
  return user ? `${user.first_name} ${user.last_name}` : ''
})
</script>
